import getDateInUTC from '@components/agile/utils/GetDateUtils';
import { INDEFINITE_TIME } from '@constants/strings';

/** Convert a timestamp (epoch ms) to a YYYY-MM-DD string for DatePicker. */
export function timestampToDateStr(ts: number): string {
  return getDateInUTC(ts, 'YYYY-MM-DD');
}

/** Convert a YYYY-MM-DD string back to epoch ms (start of day UTC). */
export function dateStrToTimestamp(dateStr: string): number {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day)).getTime();
}

/** Check if an endTime represents an indefinite date. */
export function isIndefinite(endTime: number): boolean {
  return endTime >= INDEFINITE_TIME;
}

/** Check if an entry is currently active (startTime is before today UTC). */
export function isActiveEntry(startTime: number): boolean {
  const now = new Date();
  const todayUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return startTime < todayUtc;
}

/** Returns today's date as YYYY-MM-DD in UTC. */
export function getTodayStr(): string {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, '0');
  const d = String(now.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Disables dates before today (for start date pickers). */
export function isBeforeToday(dateStr: string): boolean {
  return dateStr < getTodayStr();
}

/** Creates a disabledDates function that disables dates before a given date string. */
export function isBeforeDate(minDateStr: string): (dateStr: string) => boolean {
  return (dateStr: string) => dateStr < minDateStr;
}

/** Returns the earliest allowed start date: the later of today or the macroplanogram start time. */
export function getEarliestStartDateStr(macroplanogramStartTime: number): string {
  const today = getTodayStr();
  if (macroplanogramStartTime <= 0) return today;
  const macroStart = timestampToDateStr(macroplanogramStartTime);
  return macroStart > today ? macroStart : today;
}

/** Creates a disabledDates function that disables dates outside [minDateStr, maxTimestamp]. */
export function isOutsideDateRange(minDateStr: string, maxTimestamp: number): (dateStr: string) => boolean {
  const maxDateStr = isIndefinite(maxTimestamp) ? null : timestampToDateStr(maxTimestamp);
  return (dateStr: string) => {
    if (dateStr < minDateStr) return true;
    if (maxDateStr && dateStr > maxDateStr) return true;
    return false;
  };
}
